import { useCallback, useMemo, useRef, useState } from 'react'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { MonthBucket } from './upcomingTasks.utils'

/** Abstand zwischen zwei Zeilen (`.day-group__tasks { gap: 2px }`). */
const ROW_GAP_PX = 2

/** Totzone am Mittelpunkt, damit die Lücke nicht bei jedem Pixel umspringt. */
const FLIP_DEAD_ZONE_PX = 5

export interface DayAwareStrategy {
  /** An den `SortableContext` der Seite. */
  strategy: typeof verticalListSortingStrategy
  /**
   * Bei jedem Zug-Schritt aufrufen. Entscheidet, ob die Lücke VOR oder HINTER
   * der Zielzeile aufgeht. `overRect` ist dnd-kits **gemessener** Kasten
   * (`over.rect`), nicht die lebende Lage — siehe Begründung an der Umsetzung.
   */
  updateGapDirection: (overRect: { top: number; height: number }, pointerY: number) => void
  /** Beim Anfassen und beim Ablegen aufrufen. */
  resetGapDirection: () => void
  /** `true` = Lücke hinter der Zielzeile. Liest `handleDragEnd` für die Stelle. */
  gapAfterRef: React.RefObject<boolean | null>
}

/**
 * Die Rechnung, die beim Ziehen auf *Demnächst* den Platz macht.
 *
 * Gebaut nach `segmentAwareStrategy` aus `ProjectPage/hooks/useDndComposition.ts`
 * — dort sind die Grenzen die **Abschnitte**, hier die **Tage**. Ohne eine
 * solche Strategie verschiebt `@dnd-kit/sortable` alle Zeilen einer Liste am
 * Stück; auf dieser Seite liefe der Platz dann quer über Tages- und
 * Monatsgrenzen mit, was niemand meint.
 *
 * Zwei Fälle:
 *
 * - **Gleicher Tag:** die gewohnte Verschiebung (`verticalListSortingStrategy`),
 *   aber nur für Zeilen dieses Tages. Alle anderen stehen still.
 * - **Anderer Tag:** Nur die Zeilen des ZIEL-Tages weichen, um eine Zeilenhöhe
 *   plus Reihenabstand. Der Quell-Tag bleibt, wie er ist — seine Zeile steht
 *   ja noch da (nur unsichtbar), ihr Platz bleibt reserviert.
 */
export function useDayAwareStrategy(months: MonthBucket[], flatTaskIds: string[]): DayAwareStrategy {
  /*
    Welcher Tag hält welchen Ausschnitt von `flatTaskIds`? Dieselbe Rolle wie
    `segmentBoundaries` im Projekt. Über den Index statt über den Tagesschlüssel,
    weil die Strategie von dnd-kit nur Indizes bekommt.
  */
  const dayOfIndex = useMemo(() => {
    const out = new Int32Array(flatTaskIds.length)
    let day = 0
    let at = 0
    for (const m of months) {
      for (const d of m.days) {
        if (d.tasks.length === 0) continue
        for (let i = 0; i < d.tasks.length; i++) out[at + i] = day
        at += d.tasks.length
        day++
      }
    }
    return out
  }, [months, flatTaskIds.length])

  const gapAfterRef = useRef<boolean | null>(null)
  // Der Spiegel im State ist kein Luxus: Ohne ihn rendert der SortableContext
  // nicht neu, wenn sich nur die Richtung ändert — die Lücke bliebe stehen,
  // wo sie war.
  const [gapAfter, setGapAfter] = useState<boolean | null>(null)

  /*
    **Gemessen wird gegen dnd-kits Kasten, nicht gegen die lebende Zeile.**

    Hier stand bis zum 22.09.2026 ein `getBoundingClientRect()` der Zielzeile.
    Das ist ihre Lage MIT der Verschiebung, die die Lücke gerade erzeugt — die
    Mitte wandert also unter dem Zeiger weg, sobald der Platz aufgeht, und die
    Richtung kann zurückkippen. Je höher die Zeile, desto weiter wandert sie:
    Bei Zielen mit Unteraufgaben ging das Ablegen „erst beim zweiten oder
    dritten Mal" (Nutzer, 22.09.2026).

    `over.rect` von dnd-kit steht fest — es ist der Kasten, der beim Anfassen
    gemessen wurde.
  */
  const updateGapDirection = useCallback((overRect: { top: number; height: number }, pointerY: number) => {
    const rect = { top: overRect.top, height: overRect.height, bottom: overRect.top + overRect.height }
    const mid = rect.top + rect.height / 2
    const prev = gapAfterRef.current

    if (pointerY < rect.top) gapAfterRef.current = false
    else if (pointerY > rect.bottom) gapAfterRef.current = true
    else if (prev === null) gapAfterRef.current = pointerY >= mid
    else if (prev && pointerY < mid - FLIP_DEAD_ZONE_PX) gapAfterRef.current = false
    else if (!prev && pointerY > mid + FLIP_DEAD_ZONE_PX) gapAfterRef.current = true

    if (gapAfterRef.current !== prev) setGapAfter(gapAfterRef.current)
  }, [])

  const resetGapDirection = useCallback(() => {
    gapAfterRef.current = null
    setGapAfter(null)
  }, [])

  const strategy = useMemo(() => {
    const noMove = { x: 0, y: 0, scaleX: 1, scaleY: 1 }
    return ((args: Parameters<typeof verticalListSortingStrategy>[0]) => {
      const { activeIndex, overIndex, index, activeNodeRect } = args
      if (activeIndex < 0 || overIndex < 0) return noMove

      const activeDay = dayOfIndex[activeIndex]
      const overDay = dayOfIndex[overIndex]
      const itemDay = dayOfIndex[index]

      if (activeDay === overDay) {
        // Im selben Tag: die gewohnte Verschiebung, aber nur hier drin.
        if (itemDay !== activeDay) return noMove
        return verticalListSortingStrategy(args)
      }

      // Über die Tagesgrenze: nur der Zieltag macht Platz.
      if (itemDay !== overDay) return noMove

      // Die Höhe der ZIELzeile als Maß, nicht die der gezogenen: Auf dieser
      // Seite können Zeilen unterschiedlich hoch sein (zweizeiliger Titel).
      const height = args.rects[overIndex]?.height ?? activeNodeRect?.height ?? 0
      const shift = { x: 0, y: height + ROW_GAP_PX, scaleX: 1, scaleY: 1 }

      return (gapAfterRef.current ?? true)
        ? (index > overIndex ? shift : noMove)
        : (index >= overIndex ? shift : noMove)
    }) as typeof verticalListSortingStrategy
    // `gapAfter` steht bewusst in den Abhängigkeiten, obwohl die Rechnung den
    // Ref liest: Es ist das Signal, das den SortableContext neu rendern lässt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayOfIndex, gapAfter])

  return { strategy, updateGapDirection, resetGapDirection, gapAfterRef }
}
